// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { viewStyles } from "@turnerBlueprint/Overrides/Widgets/ProductDetails/ProductDetailsView";
import { DivWrapper } from "@turnerBlueprint/XDL/constants";
import { color, Divider, hideElementOnPrint, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

viewStyles.pageContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        row-gap: 40px;
    `,
};

viewStyles.leftColumn = {
    css: css`
        overflow: hidden;
        .hide-above-md {
            ${xScreen("gte", "md")} {
                display: none;
            }
        }
    `,
};

viewStyles.topSection = {
    css: css`
        display: grid;
        grid-template-columns: 641px 1fr;
        column-gap: 22px;
        width: 100%;

        ${xScreen("lt", "lg")} {
            grid-template-columns: 543px 1fr;
        }

        ${xScreen("lt", "md")} {
            grid-template-columns: 1fr 1fr;
        }

        ${xScreen("lt", "sm")} {
            grid-template-columns: 1fr;
        }
    `,
};

viewStyles.infoDescriptionSection = {
    css: css`
        .hide-sm {
            ${xScreen("lt", "sm")} {
                display: none;
            }
        }
    `,
};

viewStyles.haveQuestionContainer = {
    css: css`
        margin-top: 32px;

        &.hide-sm {
            ${xScreen("lt", "sm")} {
                display: none;
            }
        }

        &.hide-above-md {
            margin-top: 0;
            ${xScreen("gte", "md")} {
                display: none;
            }
        }
    `,
};

viewStyles.actionsTagsSection = {
    css: css`
        > ${DivWrapper} {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
        }
        ${xScreen("lte", 767)} {
            margin-bottom: 20px;
        }
    `,
};

viewStyles.productNumbersWrapper = {
    css: css`
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        row-gap: 4px;
        column-gap: 20px;
        max-width: 580px;

        ${xScreen("lt", "md")} {
            grid-template-columns: 1fr 1fr;
            column-gap: 20px;
            max-width: none;
        }

        ${xScreen("lt", "sm")} {
            grid-template-columns: 1fr;
        }
    `,
};

viewStyles.priceActionsContainer = {
    css: css`
        display: flex;

        ${xScreen("lte", "md")} {
            flex-direction: column;

            ${Divider} {
                width: 100%;
                height: 1px;
                margin: 16px 0;
            }
        }
    `,
};
viewStyles.priceAndActions = {
    css: css`
        width: 100%;
    `,
};

viewStyles.extraElementsContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 0 0 187px;
        row-gap: 4px;

        ${xScreen("lt", "md")} {
            flex: 1;
        }
    `,
};
viewStyles.stickyWrapper = {
    css: css`
        position: fixed;
        left: 0;
        background: ${color("common.white")};
        width: 100%;
        z-index: 1300;
        transition: all 300ms ease-out;
        opacity: 1;
        min-height: 104px;
        height: auto;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.5);
        overflow: visible;

        &.stickyVisible {
            top: 0;
        }

        &.stickyHidden {
            top: -200px;
            &,
            & > * {
                max-height: 0;
                overflow: hidden;
            }
        }

        &.editingMode {
            position: unset;
        }

        ${xScreen("lte", "md")} {
            display: none;
        }

        ${hideElementOnPrint}
    `,
};

viewStyles.stickyPage = {
    css: css`
        &&&& {
            padding: 12px 16px;
            margin-bottom: 0;
        }
    `,
};

viewStyles.actionsWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: center;
        & > div:empty {
            display: none;
        }
    `,
};
viewStyles.uomWrapper = {
    css: css`
        ${xScreen("lte", "md")} {
            width: calc(100% - 94px);
        }
    `,
};
viewStyles.addToCartWrapper = {
    css: css`
        ${xScreen("lte", "md")} {
            flex-grow: 1;
        }
    `,
};
viewStyles.specificationsContainer = {
    css: css`
        & > div:first-child:empty + div {
            display: none;
        }

        &.hide-md {
            ${xScreen("lte", "md")} {
                display: none;
            }
        }

        &.hide-above-lg {
            [data-test-selector="productDetails_specifications"] {
                margin-top: 0;
            }

            ${xScreen("gt", "lg")} {
                display: none;
            }
        }
    `,
};

viewStyles.hiddenOnPrint = {
    css: css`
        ${hideElementOnPrint}
    `,
};

viewStyles.bottomSection = {
    css: css`
        ${hideElementOnPrint}
    `,
};
